import { useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  Avatar,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { FaCamera } from "react-icons/fa";
import { HiPencil } from "react-icons/hi";
import ImageCropper from "./ImageCropper";
import { UploadProfileAvatar } from "../services/backend";
import { resolveDisplayName, resolveProfileImageUrl } from "../util/profileImage";
import { useTranslation } from "react-i18next";

export default function ProfileAvatarUploader({
  userId,
  user,
  avatarUrl,
  onAvatarUpdated,
  variant = "default",
  compact = false,
  hideCameraButton = false,
  showEditBadge = false,
}) {
  const isCompact = variant === "compact";
  const isProfileCard = variant === "profileCard";
  const { t } = useTranslation();
  const toast = useToast();
  const [croppingImage, setCroppingImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const ring = useColorModeValue("brand.400", "brand.300");
  const hint = useColorModeValue("gray.600", "whiteAlpha.600");
  const btnHover = useColorModeValue("gray.100", "whiteAlpha.100");
  const titleColor = useColorModeValue("gray.900", "white");
  const avatarFrameBg = useColorModeValue("purple.50", "rgba(167,139,250,0.18)");
  const editBadgeBorder = useColorModeValue("white", "gray.900");

  const displayName = resolveDisplayName(user, t("common.you"));
  const previewUrl = avatarUrl || resolveProfileImageUrl(user);
  const hasCustomPhoto = previewUrl && !previewUrl.includes("ui-avatars.com");

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setCroppingImage(URL.createObjectURL(file));
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
    noClick: true,
    noKeyboard: true,
  });

  const handleCropDone = async (blob, fileUrl) => {
    if (!userId) return;
    setIsUploading(true);
    try {
      const file = new File([blob], "avatar.jpg", { type: blob.type || "image/jpeg" });
      const result = await UploadProfileAvatar(userId, file);
      onAvatarUpdated?.(result.profileImageUri);
      toast({
        description: t("settings.avatarUpdated"),
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "bottom-left",
      });
      URL.revokeObjectURL(fileUrl);
    } catch (error) {
      console.error("Avatar upload failed:", error);
      toast({
        description: t("settings.avatarUpdateFailed"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom-left",
      });
    } finally {
      setIsUploading(false);
      setCroppingImage(null);
    }
  };

  const avatarSize = isProfileCard
    ? compact
      ? { base: "72px", md: "64px" }
      : { base: "80px", md: "96px" }
    : isCompact
      ? "48px"
      : undefined;
  const avatarChakraSize = isProfileCard ? (compact ? "lg" : "2xl") : isCompact ? "sm" : "xl";

  const avatarInner = (
    <Avatar
      size={avatarChakraSize}
      boxSize={avatarSize}
      src={hasCustomPhoto ? previewUrl : undefined}
      name={displayName}
      bg="brand.700"
      borderWidth={isProfileCard ? "0" : isCompact ? "2px" : "3px"}
      borderColor={ring}
      boxShadow={isCompact ? "0 4px 14px rgba(52,211,153,0.18)" : undefined}
      cursor="pointer"
      onClick={open}
    />
  );

  const avatarControl = (
    <Box position="relative" flexShrink={0} {...getRootProps()}>
      <input {...getInputProps()} />
      {isProfileCard ? (
        <Box p={compact ? "4px" : "5px"} borderRadius="full" bg={avatarFrameBg}>
          {avatarInner}
        </Box>
      ) : (
        avatarInner
      )}
      {!isProfileCard && !hideCameraButton && !showEditBadge && (
        <IconButton
          aria-label={t("settings.changeAvatar")}
          icon={<FaCamera />}
          size={isCompact ? "xs" : "sm"}
          position="absolute"
          bottom={isCompact ? "-2px" : 0}
          right={isCompact ? "-2px" : 0}
          borderRadius="full"
          colorScheme="green"
          onClick={open}
          isLoading={isUploading}
        />
      )}
      {!isProfileCard && showEditBadge && (
        <Flex
          as="button"
          type="button"
          aria-label={t("settings.changeAvatar")}
          position="absolute"
          bottom="0"
          right="0"
          boxSize="16px"
          borderRadius="full"
          bg="brand.400"
          color="white"
          align="center"
          justify="center"
          border="1.5px solid"
          borderColor={editBadgeBorder}
          onClick={open}
          opacity={isUploading ? 0.7 : 1}
          _hover={{ bg: "brand.500" }}
          transition="background 0.15s"
        >
          <Icon as={HiPencil} boxSize={2.5} />
        </Flex>
      )}
    </Box>
  );

  const uploadButton = (
    <Button
      size="sm"
      variant="outline"
      borderRadius="full"
      fontWeight="medium"
      onClick={open}
      isLoading={isUploading}
      _hover={{ bg: btnHover }}
    >
      {t("settings.uploadNewPhoto")}
    </Button>
  );

  return (
    <>
      {isProfileCard ? (
        avatarControl
      ) : isCompact ? (
        avatarControl
      ) : (
        <Flex align="center" gap={4}>
          {avatarControl}
          <Box flex={1} minW={0}>
            <Text fontSize="sm" fontWeight="semibold" color={titleColor}>
              {t("settings.chatAvatar")}
            </Text>
            <Text fontSize="xs" color={hint} mt={1} lineHeight="tall">
              {t("settings.chatAvatarHint")}
            </Text>
            <Box mt={3}>{uploadButton}</Box>
          </Box>
        </Flex>
      )}

      {croppingImage && (
        <ImageCropper
          imageSrc={croppingImage}
          onCropDone={handleCropDone}
          onCancel={() => {
            URL.revokeObjectURL(croppingImage);
            setCroppingImage(null);
          }}
        />
      )}
    </>
  );
}
